import React from 'react'
import { ContainerRow, VerticalLine } from './styles'
import { BirthEvent } from '../../../BirthEvent'
import { IAdmissionRowSlots, IHealthProcedures, ITimelineMaternalInfant } from '~/interfaces'
import { AlertMessage, RowContentPart, RowInfoPart, TimelineConducts, TimelineOmics } from '~/components/shared'
import { message } from '~/services'
import { identifyBirthEventBasedOn, mapperTimelineViewByTrimesterFrom } from '~/utils/dashboard/timeline/maternalInfant'
import { byTypeEqualToAdmission, byTypeExceptAdmission, filterHealthProceduresPer, timelineFilters } from '~/utils'
import { createAdmissionsSlotsFrom } from '~/utils/dashboard/timeline/mappers'
import { useMaternalInfantHealthProcedures } from '~/context'

interface TimelineViewByTrimesterProps {
  timelineRange: [Date, Date]
  healthProcedures: IHealthProcedures[]
  onSelectEvent: (healthProcedures: IHealthProcedures[]) => void
}

export const TimelineViewByTrimester = ({
  timelineRange,
  healthProcedures,
  onSelectEvent
}: TimelineViewByTrimesterProps): JSX.Element => {
  const { selectedPregnancy, typeFiltersActive, dateFiltersActive, filterTags } = useMaternalInfantHealthProcedures()

  const applyTimelineFilters = (healthProcedures: IHealthProcedures[]): IHealthProcedures[] => {
    const searchTerms = filterTags?.filter(tag => tag.type === 'searchTerm').map(tag => tag.text)
    return timelineFilters(healthProcedures)
      .perDate(dateFiltersActive)
      .perType(typeFiltersActive)
      .perMultipleTextTerms(searchTerms).value
  }

  const filteredEvents = applyTimelineFilters(healthProcedures)

  if (!filteredEvents || filteredEvents.length <= 0) {
    return <AlertMessage>{message('maternalInfant.empty_timeline_section')}</AlertMessage>
  }

  const filteredDataWithBirthAdmissionIdentified = identifyBirthEventBasedOn(selectedPregnancy)(filteredEvents)
  const structuredData = mapperTimelineViewByTrimesterFrom(filteredDataWithBirthAdmissionIdentified, ...timelineRange)

  const handleHealthProcedures = (selectedHealthProcedures: IHealthProcedures[]) => {
    onSelectEvent(selectedHealthProcedures)
  }

  const handleAdmissions = (selectedAdmissions: IAdmissionRowSlots[]) => {
    const rawAdmission = healthProcedures.find(healthProcedure => healthProcedure.id === selectedAdmissions[0].id)
    const healthProceduresWithoutAdmissions = filterHealthProceduresPer(healthProcedures, byTypeExceptAdmission)
    const healthProceduresInRange = timelineFilters(healthProceduresWithoutAdmissions).perEventStartDate(
      timelineRange
    ).value

    if (rawAdmission) {
      const admissionSlots = createAdmissionsSlotsFrom([rawAdmission], healthProceduresInRange)
      onSelectEvent(admissionSlots[0].healthProcedures)
    }
  }

  const handleBirthEvent = (birthEvent: ITimelineMaternalInfant.IBirthAdmission) => {
    const rawEvent = healthProcedures.find(healthProcedure => healthProcedure.id === birthEvent.id)
    const healthProceduresWithoutAdmissions = filterHealthProceduresPer(healthProcedures, byTypeExceptAdmission)
    const healthProceduresInRange = timelineFilters(healthProceduresWithoutAdmissions).perEventStartDate(
      timelineRange
    ).value

    if (rawEvent) {
      const admissionSlots = createAdmissionsSlotsFrom([rawEvent], healthProceduresInRange)
      onSelectEvent(admissionSlots[0].healthProcedures)
    }
  }

  return (
    <>
      {structuredData?.trimester.map((trimester, trimesterIndex, trimesterArray) => {
        return (
          <React.Fragment key={`trimester-${trimester.id}`}>
            <ContainerRow>
              <RowInfoPart isFilled={true} period={`${trimester.id}º Tri. (${trimester.totalSlots})`}>
                <VerticalLine isFirst={trimesterIndex === 0} isLast={false} />
              </RowInfoPart>
              <RowContentPart isFull={true} isFilled={false}>
                {/* HEADER ROW IS EMPTY */}
              </RowContentPart>
            </ContainerRow>

            {trimester.weeks.map((week, weekIndex: number, weekArray) => {
              const rowSlots = week.rowSlots
              const healthProceduresWithoutAdmissions = filterHealthProceduresPer(rowSlots, byTypeExceptAdmission)
              const admissions = filterHealthProceduresPer(rowSlots, byTypeEqualToAdmission)
              const isBirthAdmission = admissions[0]?.isBirthAdmission

              return (
                <ContainerRow key={`${trimesterIndex}-${weekIndex}`}>
                  <RowInfoPart isFilled={false} period={`${week.id}ª Sem.`}>
                    <VerticalLine
                      isFirst={false}
                      isLast={trimesterIndex === trimesterArray.length - 1 && weekIndex === weekArray.length - 1}
                    />
                  </RowInfoPart>

                  <RowContentPart isFull={false} isFilled={false}>
                    {/* CONTENT ROW */}
                    {healthProceduresWithoutAdmissions.length > 0 && (
                      <TimelineOmics
                        period="weekly"
                        healthProcedures={healthProceduresWithoutAdmissions}
                        handleClick={handleHealthProcedures}
                      />
                    )}

                    {!isBirthAdmission && admissions.length > 0 && (
                      <TimelineConducts healthProcedures={admissions} period="weekly" handleClick={handleAdmissions} />
                    )}

                    {isBirthAdmission && (
                      <BirthEvent
                        birthAdmission={admissions[0] as ITimelineMaternalInfant.IBirthAdmission}
                        birthType={selectedPregnancy.typeOfDelivery}
                        handleClick={handleBirthEvent}
                      />
                    )}
                  </RowContentPart>
                </ContainerRow>
              )
            })}
          </React.Fragment>
        )
      })}
    </>
  )
}
